import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { trackPageView } from './analytics';

interface UsePageViewTrackingOptions {
  pageName?: string;
  additionalParams?: Record<string, string | number | boolean | undefined>;
  enabled?: boolean;
}

/**
 * Custom hook for tracking page views with Google Analytics
 * Handles SSR gracefully by only executing on the client side
 *
 * @param options - Configuration options for page view tracking
 * @param options.pageName - Custom page name (defaults to router pathname)
 * @param options.additionalParams - Additional parameters to include with the event
 * @param options.enabled - Whether tracking is enabled (defaults to true)
 *
 * @example
 * // Basic usage - tracks current page
 * usePageViewTracking();
 *
 * @example
 * // Custom page name with additional parameters
 * usePageViewTracking({
 *   pageName: 'wishlist_page',
 *   additionalParams: { category: 'diamond' }
 * });
 *
 * @example
 * // Conditional tracking
 * usePageViewTracking({
 *   enabled: process.env.NODE_ENV === 'production'
 * });
 */
export const usePageViewTracking = ({
  pageName,
  additionalParams = {},
  enabled = true,
}: UsePageViewTrackingOptions = {}) => {
  const router = useRouter();
  const hasTracked = useRef(false);

  useEffect(() => {
    // Only execute on client side and when enabled
    if (typeof window === 'undefined' || !enabled) {
      return;
    }

    // Prevent duplicate tracking calls (handles React Strict Mode and re-renders)
    if (hasTracked.current) {
      return;
    }

    // Use provided page name or fallback to router pathname
    const currentPageName = pageName || router.pathname || '/';
    const currentUrl = router.asPath || '/';

    // Track the page view
    trackPageView(currentPageName, currentUrl, additionalParams);
    hasTracked.current = true;
  }, [pageName, router.pathname, router.asPath, additionalParams, enabled]);
};
